import { z } from "zod";

import { SavedViewsQuery } from "lib/generated/app-service-gql/graphql";
import {
  DocumentSavedView,
  NoticeSavedView,
  NoticeSemanticSavedView,
} from "lib/generated/saved-views/viewConfiguration";
import { SortStateSchema } from "../search/types";
import { SavedViewDataType, SavedViewSortBy, SavedViewSortOrder } from "./graphQLEnums";
import { CountryRegionSchema } from "./models";

export const savedViewDataType = z.enum(SavedViewDataType);
export type SavedViewDataTypeUnion = `${SavedViewDataType}`;

/**
 * Helper to convert a JSON string into a Zod-validated object
 * https://github.com/colinhacks/zod/discussions/2215
 */
const jsonString = <T extends z.ZodType>(schema: T) =>
  z
    .string()
    .transform((str, ctx) => {
      try {
        return JSON.parse(str);
      } catch (error) {
        ctx.addIssue({
          code: "custom",
          message: "Invalid JSON",
          fatal: true,
          input: str,
        });
        return z.NEVER;
      }
    })
    .pipe(schema);

export const TenderStagesV2 = z.enum([
  "PIPELINE",
  "PRE_TENDER_V2",
  "STALE_PRE_TENDER_V2",
  "CANCELLED_PRE_TENDER",
  "OPEN_TENDER",
  "CLOSED_TENDER",
  "CANCELLED_TENDER",
  "UNKNOWN_TENDER",
  "AWARDED_CONTRACT",
  "EXPIRED_CONTRACT",
  "CANCELLED_AWARD",
]);

/** @deprecated Don't directly use, use either DateFilterSchema or ExpiryDateFilterSchema instead */
const DateFilterSchemaBase = z.strictObject({
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  relativeFrom: z.string().optional(),
  relativeTo: z.string().optional(),
});

export const DateFilterSchema = DateFilterSchemaBase.optional().default({});

export const ExpiryDateFilterSchema = DateFilterSchemaBase.extend({
  hideNulls: z.boolean().optional(),
})
  .optional()
  .default({});

const savedViewGeneric = z.object({
  id: z.uuid(),
  dataType: savedViewDataType,
  name: z.string(),
  description: z.string().nullable(),
  view: z.unknown(),
  pinnedRank: z.number().nullable(),
  isStandardView: z.boolean(),
  owner: z
    .object({
      guid: z.uuid(),
      firstName: z.string(),
      lastName: z.string(),
      active: z.boolean(),
    })
    .nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime().nullable(),
});
export type SavedViewGeneric = z.infer<typeof savedViewGeneric>;

// Notice views are served camelCase by the API (app-service converts them from
// the snake_case DB form), so we parse camelCase here.
export const savedNoticeViewFilters = z.object({
  sortOrder: SortStateSchema.optional().default({ field: "relevance_score", order: "DESC" }),
  tableSettings: z
    .object({
      columns: z.array(
        z.object({
          field: z.string(),
        }),
      ),
    })
    .optional()
    .default({ columns: [] }),
  filterSettings: z.unknown(), // TODO: Define notice filter settings
});

export const savedNoticeView = z.object({
  ...savedViewGeneric.shape,
  dataType: z.literal("notice"),
  view: jsonString(savedNoticeViewFilters),
});
// The runtime schema parses the `view` loosely (filterSettings is unvalidated JSON), but
// the exported type uses the generated `NoticeSavedView` so callers get the real, typed
// view shape instead of `unknown`. app-service validates the shape on write.
export type SavedNoticeView = Omit<z.infer<typeof savedNoticeView>, "view"> & {
  view: NoticeSavedView;
};

export const savedDocumentViewFilters = z.object({
  sortOrder: SortStateSchema,
  tableSettings: z.object({
    columns: z.array(
      z.object({
        field: z.string(),
      }),
    ),
  }),
  filterSettings: z.object({
    signalScore: z.array(z.enum(["None", "Low", "Medium", "High"])),
    title: z.string(),
    organisationIds: z.array(z.string()).optional(),
    buyerTypes: z.array(z.string()).optional(),
    buyerListIds: z.array(z.string()).optional(),
    buyerCountryRegions: CountryRegionSchema.optional(),
    keywords: z.array(z.string()).optional(),
    categories: z.array(z.string()).optional(),
    publishDate: z
      .object({
        // TODO: Remove this extra layer of nesting
        filter: DateFilterSchema,
      })
      .optional(),
  }),
});

export const savedDocumentView = z.object({
  ...savedViewGeneric.shape,
  dataType: z.literal("document"),
  view: jsonString(savedDocumentViewFilters),
});
export type SavedDocumentView = z.infer<typeof savedDocumentView>;

export const NoticeSemanticColumnFieldEnum = z.enum([
  "relevanceScore",
  "title",
  "stage",
  "value",
  "suppliers",
  "actions",
  "awardDate",
  "closeDate",
  "expiryDate",
  "publishDate",
]);
export type NoticeSemanticColumnField = z.infer<typeof NoticeSemanticColumnFieldEnum>;
export const savedNoticeSemanticViewFilters = z.strictObject({
  sortOrder: z.strictObject({
    field: NoticeSemanticColumnFieldEnum,
    order: z.enum(["ASC", "DESC"]),
  }),
  tableSettings: z.strictObject({
    columns: z.array(
      z.strictObject({
        field: NoticeSemanticColumnFieldEnum,
      }),
    ),
  }),
  filterSettings: z.strictObject({
    topicSearch: z.string().default(""),
    stage: z.array(TenderStagesV2).optional().default([]),
    buyerIds: z.array(z.uuid()).optional().default([]),
    buyerListIds: z.array(z.uuid()).optional().default([]),
    buyerCategories: z.array(z.uuid()).optional().default([]),
    supplierIds: z.array(z.uuid()).optional().default([]),
    frameworkIds: z.array(z.uuid()).optional().default([]),
    publishDate: DateFilterSchema,
    expiryDate: ExpiryDateFilterSchema,
    value: z
      .strictObject({
        from: z.coerce.number().optional(),
        to: z.coerce.number().optional(),
        hideNulls: z.boolean().optional(),
      })
      .default({}),
  }),
});

export const savedNoticeSemanticView = z.object({
  ...savedViewGeneric.shape,
  dataType: z.literal("noticeSemantic"),
  view: jsonString(savedNoticeSemanticViewFilters),
});
export type SavedNoticeSemanticView = z.infer<typeof savedNoticeSemanticView>;

export const savedViewUnion = z.discriminatedUnion("dataType", [
  savedNoticeView,
  savedDocumentView,
  savedNoticeSemanticView,
]);
export type SavedViewUnion = z.infer<typeof savedViewUnion>;

/** Maps a saved-view `dataType` to its view-configuration shape (mirrors `SavedViewResults`). */
export type ViewConfigForDataType<D extends SavedViewDataType> = D extends "document"
  ? DocumentSavedView
  : D extends "noticeSemantic"
    ? NoticeSemanticSavedView
    : NoticeSavedView;

/**
 * A persisted saved view as returned by the `savedViews` GraphQL query, with the `view`
 * JSON string parsed into the typed config for its `dataType`. Generic over the dataType
 * and defaulting to notice, so callers that know the type (e.g. `useAllViews(Notice)`)
 * get the precise `view` shape, while the common notice usage stays `SavedViewType`.
 * Derived from the generated query result type so it stays in sync with the API.
 */
export type SavedViewType<D extends SavedViewDataType = SavedViewDataType.Notice> = Omit<
  SavedViewsQuery["savedViews"]["results"][number],
  "view" | "__typename"
> & { view: ViewConfigForDataType<D> };

export const savedViewFilters = z.object({
  dataTypes: z.array(savedViewDataType).optional(),
  order: z.enum(SavedViewSortOrder).optional(),
  orderBy: z.enum(SavedViewSortBy).optional(),
  ownerIds: z.array(z.uuid()).optional(),
  pinned: z.boolean().optional(),
  text: z.string().optional(),
});
export type SavedViewFilters = z.infer<typeof savedViewFilters>;
